import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Plus, Trash2, Rocket, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function Releases({ project }) {
  const { openIssue } = useApp();
  const [releases, setReleases] = useState([]);
  const [issues, setIssues] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", start_date: "", release_date: "" });

  const load = async () => {
    const [r, i] = await Promise.all([
      api.get("/releases", { params: { project_id: project.id } }),
      api.get("/issues", { params: { project_id: project.id } }),
    ]);
    setReleases(r.data);
    setIssues(i.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  const create = async () => {
    if (!form.name.trim()) return;
    await api.post("/releases", {
      project_id: project.id,
      name: form.name,
      description: form.description,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      release_date: form.release_date ? new Date(form.release_date).toISOString() : null,
    });
    toast.success("Release created");
    setOpen(false); setForm({ name: "", description: "", start_date: "", release_date: "" });
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete release?")) return;
    await api.delete(`/releases/${id}`);
    load();
  };
  const setState = async (r, state) => {
    await api.patch(`/releases/${r.id}`, { state });
    toast.success(`Marked ${state}`);
    load();
  };
  const assignIssue = async (issueId, releaseId) => {
    await api.patch(`/issues/${issueId}`, { fix_version_id: releaseId });
    load();
  };

  return (
    <div className="p-6 max-w-[1200px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Releases</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Track versions, their scope, and delivery dates.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-release-btn" className="gap-1.5"><Plus className="w-4 h-4" /> New release</Button>
      </div>
      {releases.length === 0 ? (
        <Card className="p-16 text-center">
          <Rocket className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-lg font-semibold">No releases yet</div>
          <div className="text-sm text-muted-foreground mt-1">Create a release to group issues by version.</div>
        </Card>
      ) : (
        <div className="space-y-3">
          {releases.map((r) => {
            const scope = issues.filter((i) => i.fix_version_id === r.id);
            const done = scope.filter((i) => i.status === "done").length;
            const pct = scope.length ? Math.round((done / scope.length) * 100) : 0;
            return (
              <Card key={r.id} className="p-4" data-testid={`release-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-display text-lg font-semibold">{r.name}</div>
                      <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-mono uppercase",
                        r.state === "released" ? "bg-emerald-500/10 text-emerald-500" :
                        r.state === "archived" ? "bg-slate-500/10 text-slate-500" : "bg-secondary text-muted-foreground")}>{r.state}</span>
                      {r.release_date ? <span className="text-xs text-muted-foreground">· ships {new Date(r.release_date).toLocaleDateString()}</span> : null}
                    </div>
                    {r.description ? <div className="text-sm text-muted-foreground mt-0.5">{r.description}</div> : null}
                  </div>
                  {r.state !== "released" ? (
                    <Button size="sm" variant="outline" onClick={() => setState(r, "released")} data-testid={`release-mark-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark released</Button>
                  ) : null}
                  <Button size="icon" variant="ghost" onClick={() => del(r.id)} data-testid={`release-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="text-xs text-muted-foreground font-mono w-16">{done}/{scope.length}</div>
                  <Progress value={pct} className="h-1.5 flex-1" />
                  <div className="text-xs font-mono">{pct}%</div>
                </div>
                {scope.length > 0 ? (
                  <div className="mt-3 space-y-1 border-t border-border pt-3">
                    {scope.map((i) => (
                      <button key={i.id} onClick={() => openIssue(i.id)} className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-accent transition-colors text-xs">
                        <span className="font-mono text-muted-foreground w-16">{i.key}</span>
                        <span className="flex-1 truncate">{i.summary}</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border status-${i.status}`}>{i.status}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-release-dialog">
          <DialogHeader><DialogTitle className="font-display">New release</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="v1.0" data-testid="nr-name" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="nr-desc" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Release</Label><Input type="date" value={form.release_date} onChange={(e) => setForm({ ...form, release_date: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={create} data-testid="nr-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
